import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from '../store/store';
import { catalogApi, CatalogItem, CatalogCategory, Catalog } from '../services/catalogApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { WhatsappPhone } from '../store/services/whatsappApi';
import MainLayout from '../components/layout/MainLayout';

const CatalogManagement: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [selectedPhoneId, setSelectedPhoneId] = useState<number | null>(null);
  const [syncInProgress, setSyncInProgress] = useState<boolean>(false);
  const [syncSuccess, setSyncSuccess] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'items' | 'categories' | 'catalogs'>('catalogs');
  
  // Category state
  const [categoryModalOpen, setCategoryModalOpen] = useState<boolean>(false);
  const [categoryForm, setCategoryForm] = useState<{name: string; description: string}>({name: '', description: ''});
  const [categoryFormErrors, setCategoryFormErrors] = useState<{name?: string; description?: string}>({});

  // Catalog state
  const [catalogModalOpen, setCatalogModalOpen] = useState<boolean>(false);
  const [catalogForm, setCatalogForm] = useState<{name: string}>({name: ''});
  const [catalogFormErrors, setCatalogFormErrors] = useState<{name?: string}>({});
  const [catalogCreationError, setCatalogCreationError] = useState<string | null>(null);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  
  // Use RTK Query hooks
  const { data: whatsappPhones, isLoading: phonesLoading } = catalogApi.useGetWhatsappPhonesQuery();
  const { data: catalogItems, isLoading: itemsLoading, refetch: refetchItems } = 
    catalogApi.useGetCatalogItemsQuery({ whatsapp_phone_id: selectedPhoneId || undefined });
  const { data: categories, isLoading: categoriesLoading, refetch: refetchCategories } = 
    catalogApi.useGetCatalogCategoriesQuery();
  const [syncCatalog] = catalogApi.useSyncCatalogMutation();
  const [deleteCatalogItem] = catalogApi.useDeleteCatalogItemMutation();
  const [createCategory] = catalogApi.useCreateCatalogCategoryMutation();
  const [updateCategory] = catalogApi.useUpdateCatalogCategoryMutation();
  const [deleteCategory] = catalogApi.useDeleteCatalogCategoryMutation();
  const { data: catalogs, isLoading: catalogsLoading, refetch: refetchCatalogs } = catalogApi.useGetCatalogsQuery();
  const { data: commerceSettings } = catalogApi.useGetCommerceSettingsQuery(selectedPhoneId || 0, { skip: !selectedPhoneId });
  const { data: availableCatalogs, isLoading: availableCatalogsLoading, refetch: refetchAvailableCatalogs } = catalogApi.useGetAvailableCatalogsQuery(selectedPhoneId || 0, { skip: !selectedPhoneId });
  const [createCatalog] = catalogApi.useCreateCatalogMutation();
  const [linkCatalog] = catalogApi.useLinkCatalogMutation();
  
  // Set first phone as default when data loads
  useEffect(() => {
    if (whatsappPhones && whatsappPhones.length > 0 && !selectedPhoneId) {
      setSelectedPhoneId(whatsappPhones[0].id);
    }
  }, [whatsappPhones, selectedPhoneId]);

  // Set first catalog as default when catalogs load
  useEffect(() => {
    if (catalogs && catalogs.length > 0 && !selectedCatalogId) {
      setSelectedCatalogId(catalogs[0].id);
    }
  }, [catalogs, selectedCatalogId]);
  
  // Handle phone selection
  const handlePhoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPhoneId(parseInt(e.target.value));
  };
  
  // Handle catalog sync
  const handleSyncCatalog = async () => {
    if (!selectedPhoneId || !selectedCatalogId) return;

    try {
      setSyncInProgress(true);
      await syncCatalog({ whatsapp_phone_id: selectedPhoneId, catalog_id: selectedCatalogId });
      setSyncSuccess(true);
      setTimeout(() => setSyncSuccess(false), 3000);
    } catch (error) {
      console.error('Error syncing catalog:', error);
    } finally {
      setSyncInProgress(false);
    }
  };

  // Handle catalog linking
  const handleLinkCatalog = async (catalogId: string) => {
    if (!selectedPhoneId) {
      alert('Please select a WhatsApp phone first');
      return;
    }

    try {
      await linkCatalog({ whatsapp_phone_id: selectedPhoneId, catalog_id: catalogId }).unwrap();
      alert('Catalog linked successfully!');
      refetchAvailableCatalogs();
    } catch (error: any) {
      console.error('Error linking catalog:', error);
      alert(error?.data?.detail || 'Failed to link catalog');
    }
  };
  
  // Handle item deletion
  const handleDeleteItem = async (itemId: number) => {
    if (window.confirm('Are you sure you want to delete this catalog item?')) {
      try {
        await deleteCatalogItem(itemId);
        refetchItems();
      } catch (error) {
        console.error('Error deleting catalog item:', error);
      }
    }
  };
  
  // Handle category input change
  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (categoryFormErrors[name as keyof typeof categoryFormErrors]) {
      setCategoryFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };
  
  // Open category modal for editing
  const handleEditCategory = (category: CatalogCategory) => {
    setCategoryForm({
      name: category.name,
      description: category.description || ''
    });
    setEditingCategoryId(category.id);
    setCategoryModalOpen(true);
  };
  
  // Open category modal for creating
  const handleAddCategory = () => {
    setCategoryForm({ name: '', description: '' });
    setEditingCategoryId(null);
    setCategoryModalOpen(true);
  };
  
  // Handle category deletion
  const handleDeleteCategory = async (categoryId: number) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(categoryId);
        refetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };
  
  // Handle category form submission
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const errors: {name?: string; description?: string} = {};

    if (!categoryForm.name) {
      errors.name = 'Category name is required';
    }

    if (Object.keys(errors).length > 0) {
      setCategoryFormErrors(errors);
      return;
    }

    try {
      if (editingCategoryId) {
        // Update existing category
        await updateCategory({
          id: editingCategoryId,
          name: categoryForm.name,
          description: categoryForm.description
        }).unwrap();
      } else {
        // Create new category
        await createCategory({
          name: categoryForm.name,
          description: categoryForm.description
        }).unwrap();
      }

      // Reset form and close modal
      setCategoryForm({ name: '', description: '' });
      setCategoryModalOpen(false);
      setEditingCategoryId(null);

      // Refetch categories
      refetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  // Handle catalog input change
  const handleCatalogInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCatalogForm(prev => ({ ...prev, [name]: value }));

    // Clear error when field is edited
    if (catalogFormErrors.name) {
      setCatalogFormErrors({});
    }
  };

  // Open catalog modal for creating
  const handleAddCatalog = () => {
    setCatalogForm({ name: '' });
    setCatalogFormErrors({});
    setCatalogCreationError(null);
    setCatalogModalOpen(true);
  };

  // Handle catalog form submission
  const handleCatalogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const errors: {name?: string} = {};

    if (!catalogForm.name) {
      errors.name = 'Catalog name is required';
    }

    if (Object.keys(errors).length > 0) {
      setCatalogFormErrors(errors);
      return;
    }

    try {
      if (!selectedPhoneId) {
        alert('Please select a WhatsApp phone first');
        return;
      }

      setCatalogCreationError(null);

      await createCatalog({
        name: catalogForm.name,
        whatsapp_phone_id: selectedPhoneId
      }).unwrap();

      // Reset form and close modal
      setCatalogForm({ name: '' });
      setCatalogModalOpen(false);

      // Refetch catalogs
      refetchCatalogs();
    } catch (error: any) {
      console.error('Error creating catalog:', error);
      const errorMessage = error?.data?.detail || error?.message || 'Failed to create catalog. Please try again.';
      setCatalogCreationError(errorMessage);
    }
  };
  
  // Render availability badge
  const renderAvailabilityBadge = (isAvailable: boolean) => {
    return isAvailable ? (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
        In Stock
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
        Out of Stock
      </span>
    );
  };
  
  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-yellow-700">
            You need to be logged in to manage your catalog.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <MainLayout>
      <div className="container mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold">Catalog Management</h1>
            <button
              onClick={() => setActiveTab('catalogs')}
              className={`px-3 py-1 text-sm rounded ${activeTab === 'catalogs' ? 'bg-whatsapp-green text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Manage Catalogs
            </button>
          </div>
          <div className="flex space-x-2">
            {activeTab === 'items' && (
              <Link
                to="/catalog/new"
                className="px-4 py-2 bg-whatsapp-green text-white rounded hover:bg-whatsapp-dark-green"
              >
                Add New Item
              </Link>
            )}
            {activeTab === 'categories' && (
              <button
                onClick={handleAddCategory}
                className="px-4 py-2 bg-whatsapp-green text-white rounded hover:bg-whatsapp-dark-green"
              >
                Add New Category
              </button>
            )}
            {activeTab === 'catalogs' && (
              <button
                onClick={handleAddCatalog}
                className="px-4 py-2 bg-whatsapp-green text-white rounded hover:bg-whatsapp-dark-green"
              >
                Create New Catalog
              </button>
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('items')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'items' ? 'border-whatsapp-green text-whatsapp-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                Items
              </button>
              <button
                onClick={() => setActiveTab('categories')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'categories' ? 'border-whatsapp-green text-whatsapp-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                Categories
              </button>
              <button
                onClick={() => setActiveTab('catalogs')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'catalogs' ? 'border-whatsapp-green text-whatsapp-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                Catalogs
              </button>
            </nav>
          </div>
        </div>
        
        {/* Items Tab */}
        {activeTab === 'items' && (
          <>
            {/* WhatsApp Phone Selection */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="mb-4 md:mb-0">
                  <label htmlFor="phone-select" className="block text-sm font-medium text-gray-700 mb-1">
                    Select WhatsApp Phone
                  </label>
                  <select
                    id="phone-select"
                    className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                    value={selectedPhoneId || ''}
                    onChange={handlePhoneChange}
                    disabled={phonesLoading}
                  >
                    {phonesLoading ? (
                      <option>Loading phones...</option>
                    ) : whatsappPhones && whatsappPhones.length > 0 ? (
                      whatsappPhones.map((phone: WhatsappPhone) => (
                        <option key={phone.id} value={phone.id}>
                          {phone.display_name} ({phone.phone_number})
                        </option>
                      ))
                    ) : (
                      <option>No WhatsApp phones available</option>
                    )}
                  </select>
                </div>
                
                 <div className="flex flex-col space-y-2">
                   <label htmlFor="catalog-select" className="block text-sm font-medium text-gray-700">
                     Select Catalog for Sync
                   </label>
                   <select
                     id="catalog-select"
                     className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                     value={selectedCatalogId || ''}
                     onChange={(e) => setSelectedCatalogId(e.target.value || null)}
                     disabled={catalogsLoading}
                   >
                     {catalogsLoading ? (
                       <option>Loading catalogs...</option>
                     ) : catalogs && catalogs.length > 0 ? (
                       catalogs.map((catalog: Catalog) => (
                         <option key={catalog.id} value={catalog.id}>
                           {catalog.name}
                         </option>
                       ))
                     ) : (
                       <option>No catalogs available</option>
                     )}
                   </select>
                 </div>

                 <button
                   onClick={handleSyncCatalog}
                   disabled={!selectedPhoneId || !selectedCatalogId || syncInProgress}
                   className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center"
                 >
                  {syncInProgress ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    'Sync with WhatsApp'
                  )}
                </button>
              </div>
              
               {syncSuccess && (
                 <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                   Catalog synchronization started successfully!
                 </div>
               )}

               {/* Catalog Status */}
               <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                 <div className="flex items-center justify-between">
                   <div className="flex items-center">
                     <svg className="h-5 w-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                       <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                     </svg>
                     <div>
                       <p className="text-sm font-medium text-blue-800">
                         {catalogs && catalogs.length > 0 ? 'Catalog Available' : 'No Catalogs Found'}
                       </p>
                       <p className="text-sm text-blue-700">
                         {catalogs && catalogs.length > 0
                           ? `Found ${catalogs.length} catalog${catalogs.length > 1 ? 's' : ''}. Sync will use the selected catalog.`
                           : 'Create a catalog to enable synchronization.'
                         }
                       </p>
                       {commerceSettings && (
                         <div className="mt-2 text-xs text-blue-600">
                           <p>Commerce Settings: Cart {commerceSettings.settings.is_cart_enabled ? '✓' : '✗'}, Catalog {commerceSettings.settings.is_catalog_visible ? '✓' : '✗'}</p>
                         </div>
                       )}
                     </div>
                   </div>
                   {(!catalogs || catalogs.length === 0) && (
                     <button
                       onClick={() => setActiveTab('catalogs')}
                       className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                     >
                       Create Catalog
                     </button>
                   )}
                 </div>
               </div>
            </div>
            
             {/* Catalog Items */}
             <div className="bg-white shadow overflow-hidden sm:rounded-md">
               <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
                 <h3 className="text-lg leading-6 font-medium text-gray-900">
                   Catalog Items
                 </h3>
                 <p className="mt-1 text-sm text-gray-500">
                   Manage your WhatsApp catalog items
                 </p>
               </div>

               {(!catalogs || catalogs.length === 0) && (
                 <div className="px-4 py-3 bg-yellow-50 border-l-4 border-yellow-400">
                   <div className="flex items-center justify-between">
                     <div>
                       <p className="text-sm text-yellow-700">
                         <strong>No catalogs found.</strong> You need to create a catalog before you can sync items.
                       </p>
                     </div>
                     <button
                       onClick={() => setActiveTab('catalogs')}
                       className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                     >
                       Create Catalog
                     </button>
                   </div>
                 </div>
               )}
              
              {itemsLoading ? (
                <LoadingSpinner />
              ) : catalogItems && catalogItems.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {catalogItems.map((item: CatalogItem) => (
                    <li key={item.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {item.images && item.images.length > 0 ? (
                            <img 
                              src={item.images[0].original_url || '/placeholder-image.png'} 
                              alt={item.name}
                              className="h-12 w-12 object-cover rounded"
                            />
                          ) : (
                            <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                              <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="ml-4">
                            <h4 className="text-lg font-medium text-gray-900">{item.name}</h4>
                            <p className="text-sm text-gray-500">{item.price} {item.currency}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          {renderAvailabilityBadge(item.is_available)}
                          <div className="flex space-x-2">
                            <Link
                              to={`/catalog/${item.id}`}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Link>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-5 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No catalog items</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating a new catalog item.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/catalog/new"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-whatsapp-green hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-green"
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      New Catalog Item
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Categories
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Manage your product categories
              </p>
            </div>
            
            {categoriesLoading ? (
              <LoadingSpinner />
            ) : categories && categories.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {categories.map((category: CatalogCategory) => (
                  <li key={category.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{category.name}</h4>
                        {category.description && (
                          <p className="text-sm text-gray-500">{category.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No categories</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a new category.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleAddCategory}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-whatsapp-green hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-green"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    New Category
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Catalogs Tab */}
        {activeTab === 'catalogs' && (
          <>
            {/* Link Existing Catalog Section */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md mb-6">
              <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Link Meta Commerce Catalog
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Connect an existing catalog from Meta Commerce Manager to this WhatsApp phone
                </p>
              </div>

              {availableCatalogsLoading ? (
                <div className="px-4 py-5">
                  <LoadingSpinner />
                </div>
              ) : availableCatalogs && availableCatalogs.available_catalogs && availableCatalogs.available_catalogs.length > 0 ? (
                <div className="px-4 py-5">
                  {availableCatalogs.linked_catalog_id && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">
                        <strong>Currently Linked:</strong> {availableCatalogs.linked_catalog_id}
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {availableCatalogs.available_catalogs.map((catalog) => (
                      <div key={catalog.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{catalog.name}</h4>
                          <p className="text-xs text-gray-500">ID: {catalog.id}</p>
                        </div>
                        <button
                          onClick={() => handleLinkCatalog(catalog.id)}
                          disabled={availableCatalogs.linked_catalog_id === catalog.id}
                          className={`px-3 py-1 text-sm rounded ${
                            availableCatalogs.linked_catalog_id === catalog.id
                              ? 'bg-green-100 text-green-800 cursor-not-allowed'
                              : 'bg-whatsapp-green text-white hover:bg-whatsapp-dark-green'
                          }`}
                        >
                          {availableCatalogs.linked_catalog_id === catalog.id ? 'Linked' : 'Link This Catalog'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="px-4 py-5 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No catalogs found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Create a catalog in Meta Commerce Manager first, then refresh this page.
                  </p>
                  <div className="mt-4">
                    <a
                      href="https://business.facebook.com/commerce"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Open Meta Commerce Manager
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Database Catalogs Section */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Database Catalogs
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Catalogs stored in your database
                </p>
              </div>

            {catalogsLoading ? (
              <LoadingSpinner />
            ) : catalogs && catalogs.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {catalogs.map((catalog: Catalog) => (
                  <li key={catalog.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{catalog.name}</h4>
                        <p className="text-sm text-gray-500">ID: {catalog.id}</p>
                        <p className="text-sm text-gray-500">Created: {new Date(catalog.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => setSelectedCatalogId(catalog.id)}
                          className={`px-3 py-1 text-sm rounded ${selectedCatalogId === catalog.id ? 'bg-whatsapp-green text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                          {selectedCatalogId === catalog.id ? 'Selected' : 'Select for Sync'}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No catalogs</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a new catalog.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleAddCatalog}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-whatsapp-green hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-green"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Catalog
                  </button>
                </div>
              </div>
            )}
          </div>
          </>
        )}

        {/* Category Modal */}
        {categoryModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">{editingCategoryId ? 'Edit Category' : 'Add New Category'}</h2>
                <button
                  type="button"
                  onClick={() => setCategoryModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleCategorySubmit}>
                <div className="mb-4">
                  <label htmlFor="category-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Category Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="category-name"
                    name="name"
                    value={categoryForm.name}
                    onChange={handleCategoryInputChange}
                    className={`w-full px-3 py-2 border ${categoryFormErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
                    placeholder="e.g., Electronics, Clothing, etc."
                  />
                  {categoryFormErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{categoryFormErrors.name}</p>
                  )}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="category-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    id="category-description"
                    name="description"
                    value={categoryForm.description}
                    onChange={handleCategoryInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                    placeholder="Brief description of this category"
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setCategoryModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-dark-green"
                  >
                    {editingCategoryId ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Catalog Modal */}
        {catalogModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Create New Catalog</h2>
                <button
                  type="button"
                  onClick={() => setCatalogModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCatalogSubmit}>
                <div className="mb-4">
                  <label htmlFor="catalog-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Catalog Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="catalog-name"
                    name="name"
                    value={catalogForm.name}
                    onChange={handleCatalogInputChange}
                    className={`w-full px-3 py-2 border ${catalogFormErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green`}
                    placeholder="e.g., Main Catalog, Electronics, etc."
                  />
                  {catalogFormErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{catalogFormErrors.name}</p>
                  )}
                </div>

                {catalogCreationError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex">
                      <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-700">{catalogCreationError}</p>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Catalogs will be created for the selected WhatsApp phone: {whatsappPhones?.find(p => p.id === selectedPhoneId)?.display_name || 'None selected'}
                  </p>
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setCatalogModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-dark-green"
                    disabled={!selectedPhoneId}
                  >
                    Create Catalog
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* WhatsApp Catalog Info */}
        <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">About WhatsApp Catalog</h2>
          <p className="text-gray-700 mb-4">
            The WhatsApp Catalog feature allows you to showcase your products directly in WhatsApp.
            Customers can browse your catalog and select products to purchase.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-lg mb-2">Advantages</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Showcase products directly in WhatsApp</li>
                <li>Customers can browse and select items</li>
                <li>Seamless shopping experience</li>
                <li>Automatic synchronization with WhatsApp</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-lg mb-2">Limitations</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Maximum 500 items per catalog</li>
                <li>Image size: 500KB to 5MB</li>
                <li>Image format: JPG, JPEG, PNG</li>
                <li>Name limit: 150 characters</li>
                <li>Description limit: 1000 characters</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CatalogManagement;
